import { useFocusEffect } from '@react-navigation/native';
import {
  Box,
  Button,
  IButtonProps,
  Pressable,
  Spinner,
  Text,
  useDisclose,
  useToast,
  VStack,
} from 'native-base';
import React, { useCallback, useMemo, useState } from 'react';
import { Linking, Platform } from 'react-native';

import CPAlert from 'app/components/CPAlert';
import HTML from 'app/components/HTML';
import OfferCard from 'app/components/OfferCard';
import ReservationModifyCancelSheet from 'app/components/ReservationModifyCancelSheet';
import ReservationRequirementLabel from 'app/components/ReservationRequirementLabel';
import ReservationSummary from 'app/components/ReservationSummary';
import TicketCarouselModal from 'app/components/TicketCarouselModal';
import { AttractionContent } from 'app/hooks/useAttraction';
import useAttractionOffline from 'app/hooks/useAttractionOffline';
import useAuth from 'app/hooks/useAuth';
import useGuest from 'app/hooks/useGuest';
import useGuestDataQuery from 'app/hooks/useGuestDataQuery';
import { ProductContent } from 'app/hooks/useProduct';
import usePromptReview from 'app/hooks/usePromptReview/usePromptReview';
import useRootNavigation from 'app/hooks/useRootNavigation';
import { useI18n } from 'app/providers/I18nProvider';
import ReservationRedirectModal from 'app/screens/AttractionScreen/ReservationRedirectModal';
import Analytics, { Events } from 'app/services/Analytics';
import { getSharedAdmissionOffers } from 'app/services/AttractionHelper';
import {
  getReservationsByAttractionKey,
  hasAttractionTickets,
  hasPasses,
  hasReservationsForAllPassesByAttractionKey,
} from 'app/services/EntitlementHelper';
import {
  AttractionConfig,
  EntitlementReservation,
  ReservationRequirement,
} from 'app/services/GuestCenterService.types';
import {
  createReservationTextTranslationHelper,
  ReservationTextHelper,
  TranslationHelper,
} from 'app/services/I18n';
import Logger from 'app/services/Logger';
import * as ReservationHelper from 'app/services/ReservationHelper';
import { ReservationActions } from 'app/services/ReservationHelper';

export type AttractionDetailsHowToVisitProps = {
  attractionContent: AttractionContent;
  attractionConfig: AttractionConfig;
  productContent: ProductContent;
};

export default function AttractionDetailsHowToVisit({
  attractionContent,
  attractionConfig,
  productContent,
}: AttractionDetailsHowToVisitProps) {
  const { t } = useI18n();
  const { activeEntitlement } = useGuest();
  const { user } = useAuth();
  const userData = useGuestDataQuery();
  const attractionOffline = useAttractionOffline({ attractionKey: attractionConfig.attractionKey });
  const toast = useToast();
  const promptReview = usePromptReview();
  const navigation = useRootNavigation();
  const reservationRedirectModal = useDisclose();
  const viewTicketsModal = useDisclose();
  const reservationModifyCancelSheet = useDisclose();
  const [activeModifyCancelReservation, setModifyCancelReservation] =
    useState<EntitlementReservation | null>(null);
  const entitlement =
    activeEntitlement?.productContentKey === productContent.key ? activeEntitlement : undefined;

  const rt = useMemo(
    () =>
      createReservationTextTranslationHelper(t, {
        attraction: attractionContent,
        product: productContent,
      }),
    [t, attractionContent, productContent]
  );
  const handleDisabledBtnPress = useCallback(() => {
    const id = 'link_tickets';
    if (toast.isActive(id)) return;
    toast.show({ id, title: t('app_error_link_tickets') });
  }, [t, toast]);

  const openCPTickets = useCallback(() => {
    viewTicketsModal.onOpen();
    Analytics.trackEvent(Events.AttractionViewTickets, {
      productKey: productContent.key ?? '',
      attractionKey: attractionContent.key,
    });
  }, [attractionContent.key, productContent.key, viewTicketsModal]);

  const userActionBtns = useMemo(
    () =>
      getUserActionButtons({
        attractionConfig,
        productContent,
        attractionContent,
        userData,
        reservationRedirectModal,
        openCPTickets,
        t,
        navigation,
      }),
    [
      attractionConfig,
      productContent,
      attractionContent,
      userData,
      reservationRedirectModal,
      openCPTickets,
      t,
      navigation,
    ]
  );

  useFocusEffect(() => {
    if (promptReview.previouslyPrompted()) {
      promptReview.promptForReview();
    }
  });

  if (!attractionConfig) return <Spinner />;

  const { attractionKey } = attractionConfig;
  const disableUserActionBtns =
    !user || !entitlement || !hasAttractionTickets(entitlement, attractionKey) || attractionOffline;
  const isReservable = ReservationHelper.isReservable(attractionConfig);
  const isRedirect = ReservationHelper.isRedirect(attractionConfig);
  const reservations = getReservationsByAttractionKey(entitlement, attractionKey);
  const hasAttractionReservations = reservations.length > 0;
  const hasReservationsForAllPasses = hasReservationsForAllPassesByAttractionKey(
    entitlement,
    attractionKey
  );
  const hasRemainingVisit =
    entitlement?.productLineup?.attractionHasRemainingVisit(attractionKey) ?? true;
  const showInvalidTicketsMsg = !hasAttractionReservations && !hasRemainingVisit;
  const showOfflineWarning = attractionOffline && isReservable && !hasAttractionReservations;
  const canViewTickets = entitlement && hasPasses(entitlement);
  const hasTicketsModal = canViewTickets && viewTicketsModal.isOpen;
  const hasReservationRedirectModal = isReservable && isRedirect;
  const sectionLabel = (key: string) => `<strong>${rt(key).trim()}:</strong>`;

  const handleReservationRedirectContinue = () => {
    const redirectURL = ReservationHelper.redirectURL(attractionConfig, {
      barcodes: entitlement?.passes.map((pass) => pass.barcode),
    });
    Logger.debug('[AttractionDetailsHowToVisit] opening link', { redirectURL });
    Linking.openURL(redirectURL);
  };

  const handleTicketsModalClose = () => {
    viewTicketsModal.onClose();

    if (activeEntitlement?.reservationRequirement !== ReservationRequirement.Required) {
      promptReview.promptForReview();
    }
  };

  const handleModifyCancel = (res: EntitlementReservation) => {
    setModifyCancelReservation(res);
    reservationModifyCancelSheet.onOpen();
  };
  const handleModifyCancelClose = () => {
    reservationModifyCancelSheet.onClose();
  };
  const handleModify = () => {
    if (!activeModifyCancelReservation) return;
    navigation.navigate('ReservationFlow', {
      attractionKey: attractionContent.key ?? '',
      productKey: productContent.key ?? '',
      attractionName: attractionContent?.name ?? '',
      reservationKey: activeModifyCancelReservation.reservationKey ?? '',
      isModification: true,
    });
    reservationModifyCancelSheet.onClose();
  };

  return (
    <Box testID="AttractionDetailsHowToVisit">
      <VStack space="5">
        {showInvalidTicketsMsg ? (
          <InvalidTicketMessage
            attractionContent={attractionContent}
            productContent={productContent}
            t={t}
          />
        ) : (
          <>
            {showOfflineWarning && (
              <OfflineWarningMessage attractionContent={attractionContent} rt={rt} />
            )}

            {hasAttractionReservations &&
              reservations.map((res) => (
                <ReservationSummary
                  key={res.reservationKey}
                  attractionContentKey={attractionContent.key}
                  orderNumber={entitlement?.travelParty?.orderNumber || ''}
                  reservation={res}
                  onModifyCancel={handleModifyCancel}
                />
              ))}

            {!!activeModifyCancelReservation && (
              <ReservationModifyCancelSheet
                attractionContent={attractionContent}
                isOpen={reservationModifyCancelSheet.isOpen}
                reservation={activeModifyCancelReservation}
                onClose={handleModifyCancelClose}
                onModify={handleModify}
              />
            )}

            {!hasReservationsForAllPasses && (
              <VStack space="5">
                {!hasAttractionReservations && (
                  <ReservationRequirementLabel attrConfig={attractionConfig} rt={rt} />
                )}

                {!!rt('reservations', { fallback: false }) && (
                  <HTML
                    defaultTextProps={{
                      selectable: true,
                    }}
                    html={rt('reservations')}
                  />
                )}
                {isRedirect && <RedirectInfoMessage rt={rt} />}

                {userData.isFetching && <Spinner />}
                {userActionBtns.map(({ onPress, ...btn }) => {
                  return (
                    <Pressable
                      key={btn.id}
                      {...(disableUserActionBtns ? { onPress: handleDisabledBtnPress } : {})}
                    >
                      <Button
                        isDisabled={disableUserActionBtns}
                        variant="solid"
                        {...btn}
                        {...(!disableUserActionBtns ? { onPress } : {})}
                      />
                    </Pressable>
                  );
                })}
              </VStack>
            )}

            {!!rt('entry', { fallback: false }) && (
              <HTML
                defaultTextProps={{
                  selectable: true,
                }}
                html={`${sectionLabel('entry_title')} ${rt('entry', { fallback: false })}`}
              />
            )}

            {!!rt('entry_citypass_tickets', { fallback: false }) && (
              <HTML
                defaultTextProps={{
                  selectable: true,
                }}
                html={`${
                  rt('entry_citypass_tickets_title', { fallback: false })
                    ? sectionLabel('entry_citypass_tickets_title')
                    : ''
                } ${rt('entry_citypass_tickets', {
                  fallback: false,
                })} ${
                  entitlement
                    ? `<a href="#" data-trigger="view-citypass-tickets">${t(
                        'app_view_citypass_tickets'
                      )}</a>`
                    : ''
                }`}
                renderersProps={{
                  a: {
                    onPress: (event, _, htmlAttribs) => {
                      if (htmlAttribs['data-trigger'] === 'view-citypass-tickets') {
                        event.preventDefault();
                        openCPTickets();
                      }
                    },
                  },
                }}
              />
            )}
            {!!rt('changes', { fallback: false }) && (
              <HTML
                defaultTextProps={{
                  selectable: true,
                }}
                html={`${sectionLabel('changes_title')} ${rt('changes', { fallback: false })}`}
              />
            )}
            {!!rt('misc', { fallback: false }) && (
              <HTML
                defaultTextProps={{
                  selectable: true,
                }}
                html={`${sectionLabel('misc_title')} ${rt('misc', { fallback: false })}`}
              />
            )}
          </>
        )}
      </VStack>

      {/* keep outside of VStack for correct spacing */}
      {hasTicketsModal && (
        <TicketCarouselModal
          isOpen={viewTicketsModal.isOpen}
          orderNumber={entitlement.travelParty?.orderNumber}
          passes={entitlement.passes}
          onClose={handleTicketsModalClose}
        />
      )}

      {hasReservationRedirectModal && (
        <ReservationRedirectModal
          attractionContent={attractionContent}
          attractionContentKey={attractionContent.key}
          isOpen={reservationRedirectModal.isOpen}
          productContentKey={productContent.key ?? ''}
          rt={rt}
          t={t}
          onClose={reservationRedirectModal.onClose}
          onContinue={handleReservationRedirectContinue}
        />
      )}
    </Box>
  );
}

function InvalidTicketMessage({
  productContent,
  attractionContent,
  t,
}: {
  productContent: ProductContent;
  attractionContent: AttractionContent;
  t: (key: string) => string;
}) {
  const sharedAdmissionOffers = getSharedAdmissionOffers(attractionContent);

  return (
    <CPAlert status="info">
      <VStack space="2">
        <Text>{t('app_tickets_not_valid_for_attraction')}</Text>
        {sharedAdmissionOffers && sharedAdmissionOffers.length > 0 && (
          <>
            <Text>{t('app_tickets_admission_offer')}</Text>
            {sharedAdmissionOffers.map((offer) => (
              <OfferCard
                key={offer.id}
                offer={offer}
                productContentKey={productContent.key ?? ''}
              />
            ))}
          </>
        )}
      </VStack>
    </CPAlert>
  );
}

function OfflineWarningMessage({
  attractionContent,
  rt,
}: {
  attractionContent: AttractionContent;
  rt: ReservationTextHelper;
}) {
  return (
    <CPAlert status="warning">
      <HTML
        html={rt('offline_reservation_content', {
          attraction_name: attractionContent?.name ?? '',
        })}
      />
    </CPAlert>
  );
}

function RedirectInfoMessage({ rt }: { rt: ReservationTextHelper }) {
  return (
    <CPAlert mb="2" status="info">
      <HTML
        defaultTextProps={{
          selectable: true,
        }}
        html={rt('redirect_info')}
      />
    </CPAlert>
  );
}

type BtnProps = { id: string } & IButtonProps;

function getUserActionButtons({
  attractionConfig,
  productContent,
  attractionContent,
  userData,
  reservationRedirectModal,
  openCPTickets,
  t,
  navigation,
}: {
  attractionConfig: AttractionConfig;
  productContent: ProductContent;
  attractionContent: AttractionContent;
  userData: ReturnType<typeof useGuestDataQuery>;
  reservationRedirectModal: ReturnType<typeof useDisclose>;
  openCPTickets: () => void;
  t: TranslationHelper;
  navigation: ReturnType<typeof useRootNavigation>;
}) {
  if (!attractionConfig || !productContent.type || !attractionContent.reservation) return [];

  const actions = ReservationHelper.userActions(attractionConfig, productContent.type);
  const actionToButton: Record<ReservationActions, (action: ReservationActions) => BtnProps> = {
    [ReservationActions.Reserve]: (action) => ({
      id: action,
      children: t('app_make_reservations'),
      disabled: userData.isFetching,
      onPress: () => {
        if (!productContent.key || !attractionContent.key || !attractionContent.name) return;
        Analytics.trackEvent(Events.AttractionMakeReservation, {
          productKey: productContent.key ?? '',
          attractionKey: attractionContent.key,
        });
        navigation.navigate('ReservationFlow', {
          productKey: productContent.key,
          attractionKey: attractionContent.key,
          attractionName: attractionContent.name,
        });
      },
    }),
    [ReservationActions.ReserveRedirect]: (action) => ({
      id: action,
      children: t('app_make_reservations'),
      disabled: userData.isFetching,
      onPress: reservationRedirectModal.onOpen,
    }),
    [ReservationActions.ReservePhone]: (action) => {
      const [displayPhoneNumber, phoneNumber] = ReservationHelper.phoneNumber(
        attractionContent.reservation
      );
      return {
        id: action,
        children: displayPhoneNumber,
        disabled: userData.isFetching,
        onPress: () => {
          // https://stackoverflow.com/questions/76728990/linking-openurltelxxxxx-doenst-work-in-react-native-ios-0-72-ios
          const scheme = Platform.select({ ios: 'tel://', default: 'tel:' });
          Linking.openURL(scheme + (phoneNumber || displayPhoneNumber));
        },
      };
    },
    [ReservationActions.ViewTickets]: (action) => ({
      id: action,
      children: t('app_view_citypass_tickets'),
      onPress: openCPTickets,
      variant: actions.length > 1 ? 'outline' : 'solid',
    }),
  };

  return actions.map((action) => actionToButton[action](action));
}
